import { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';

import Skeleton from '@mui/material/Skeleton';

import CustomResizable from '../../components/custom-resizable';
import InvoiceFormItem from '../../components/invoice-form-item';
import Modal from '../../components/modal_dialog';
import { fetch as apiFetch } from '../../utils/api-wrapper';
import { formatMoney } from '../../utils/format-money';
import CreateCardLineItemModal from './CreateCardLineItemModal';
import LinkTransactionModal from './LinkTransactionModal';
import StatementLinesTable from './StatementLinesTable';
import { formatCard, formatDate, hasDisplayValue } from './statement-display';

import './statement-imports.scss';

const translate = (data, key, fallback = key) => {
  const value = data?.t?.read?.(key);

  return typeof value === 'string' || typeof value === 'number'
      ? value
      : fallback;
};

const buildStatementFields = (data, reconciliation) => {
  const statement = reconciliation.statement || {};
  const currency = statement.currency;

  const money = (value) => formatMoney(value, currency || null);

  return [
    {
      key: 'issuer_name',
      label: translate(
          data,
          'cc_statements.statement_imports.table.issuer',
          'Issuer'
      ),
      value: reconciliation.issuer_name,
    },
    {
      key: 'statement_date',
      label: translate(
          data,
          'cc_statements.statement_import_modal.statement_date',
          'Statement date'
      ),
      value: formatDate(statement.statement_date),
      raw: statement.statement_date,
    },
    {
      key: 'period_start',
      label: translate(
          data,
          'cc_statements.statement_import_modal.period_start',
          'Period start'
      ),
      value: formatDate(statement.period_start),
      raw: statement.period_start,
    },
    {
      key: 'period_end',
      label: translate(
          data,
          'cc_statements.statement_import_modal.period_end',
          'Period end'
      ),
      value: formatDate(statement.period_end),
      raw: statement.period_end,
    },
    {
      key: 'currency',
      label: translate(
          data,
          'cc_statements.statement_import_modal.currency',
          'Currency'
      ),
      value: currency,
    },
    {
      key: 'opening_balance',
      label: translate(
          data,
          'cc_statements.statement_import_modal.opening_balance',
          'Opening balance'
      ),
      value: money(statement.opening_balance),
      raw: statement.opening_balance,
    },
    {
      key: 'closing_balance',
      label: translate(
          data,
          'cc_statements.statement_import_modal.closing_balance',
          'Closing balance'
      ),
      value: money(statement.closing_balance),
      raw: statement.closing_balance,
    },
    {
      key: 'total_charges',
      label: translate(
          data,
          'cc_statements.statement_import_modal.total_charges',
          'Total charges'
      ),
      value: money(statement.total_charges),
      raw: statement.total_charges,
    },
    {
      key: 'total_credits',
      label: translate(
          data,
          'cc_statements.statement_import_modal.total_credits',
          'Total credits'
      ),
      value: money(statement.total_credits),
      raw: statement.total_credits,
    },
  ].filter((field) =>
      hasDisplayValue('raw' in field ? field.raw : field.value)
  );
};

export const StatementImportModal = ({
                                       data,
                                       reconciliation,
                                       onClose,
                                     }) => {
  const reconciliationId = reconciliation?.id;

  const [sourceDocument, setSourceDocument] = useState(null);
  const [documentFailed, setDocumentFailed] = useState(false);

  const [detail, setDetail] = useState(null);
  const [detailFailed, setDetailFailed] = useState(false);

  const [linkingItem, setLinkingItem] = useState(null);
  const [creatingItem, setCreatingItem] = useState(null);
  const [busyItemId, setBusyItemId] = useState(null);
  const [matchError, setMatchError] = useState(null);

  useEffect(() => {
    if (!reconciliationId) return undefined;

    let active = true;

    setSourceDocument(null);
    setDocumentFailed(false);

    apiFetch(
        `/c_card/statement-reconciliations/${reconciliationId}/document`,
        'GET',
        {},
        (response) => {
          if (!active) return;

          const url = response?.data?.url;

          setSourceDocument(url ? response.data : null);
          setDocumentFailed(!url);
        },
        () => {
          if (!active) return;

          setDocumentFailed(true);
        }
    );

    return () => {
      active = false;
    };
  }, [reconciliationId]);

  useEffect(() => {
    if (!reconciliationId) return undefined;

    let active = true;

    setDetail(null);
    setDetailFailed(false);

    apiFetch(
        `/c_card/statement-reconciliations/${reconciliationId}`,
        'GET',
        {},
        (response) => {
          if (!active) return;

          const responseDetail = response?.data || null;

          setDetail(responseDetail);
          setDetailFailed(!responseDetail);
        },
        () => {
          if (!active) return;

          setDetailFailed(true);
        }
    );

    return () => {
      active = false;
    };
  }, [reconciliationId]);

  const submitMatchChange = (item, method, body, onDone = () => {}, path = 'match') => {
    setBusyItemId(item.id);
    setMatchError(null);

    apiFetch(
        `/c_card/statement-reconciliations/${reconciliationId}/items/${item.id}/${path}`,
        method,
        body,
        (response) => {
          setBusyItemId(null);

          if (response?.data) {
            setDetail(response.data);
          }

          onDone();
        },
        (error) => {
          setBusyItemId(null);

          const code = error?.response?.data?.error;

          setMatchError(
              translate(
                  data,
                  `cc_statements.statement_reconciliation.errors.${code}`,
                  translate(
                      data,
                      'cc_statements.statement_reconciliation.errors.match_failed',
                      'The statement line could not be updated.'
                  )
              )
          );
        }
    );
  };

  const linkTransaction = (transactionId) => {
    if (!transactionId || !linkingItem) return;

    submitMatchChange(
        linkingItem,
        'PUT',
        { transaction_id: transactionId },
        () => setLinkingItem(null)
    );
  };

  const unlinkTransaction = (item) => {
    submitMatchChange(item, 'DELETE', {});
  };

  const createLineItem = (lineItem) => {
    if (!creatingItem) return;

    submitMatchChange(
        creatingItem,
        'POST',
        lineItem,
        () => setCreatingItem(null),
        'line-item'
    );
  };

  if (!reconciliation) return null;

  const fields = buildStatementFields(data, reconciliation);

  // The detail request carries the same failure fields as the list row it was
  // opened from; prefer it once it lands, and fall back to the row so the
  // reason is on screen immediately rather than after the round trip.
  const failureCode = detail?.failure_code ?? reconciliation.failure_code;
  const failed = (detail?.status ?? reconciliation.status) === 'failed';
  // An unrecognised code must not render a raw translation key at the user.
  const failureReason = failureCode
      ? translate(
          data,
          `cc_statements.statement_imports.failure_reasons.${failureCode}`,
          translate(
              data,
              'cc_statements.statement_import_modal.failure_unknown',
              'The statement could not be processed.'
          )
      )
      : translate(
          data,
          'cc_statements.statement_import_modal.failure_unknown',
          'The statement could not be processed.'
      );

  const isImage = Boolean(
      sourceDocument?.content_type?.startsWith('image/')
  );

  const isPdf =
      sourceDocument?.content_type === 'application/pdf';

  const documentPreview = () => {
    if (documentFailed) {
      return (
          <div className='alert alert-warning m-3' role='alert'>
            {translate(
                data,
                'cc_statements.statement_import_modal.document_load_failed',
                'The original statement document could not be loaded.'
            )}
          </div>
      );
    }

    if (!sourceDocument) {
      return (
          <div
              className='h-100 p-4'
              aria-busy='true'
              aria-label={translate(data, 'loading', 'Loading')}
          >
            <Skeleton
                variant='rectangular'
                height='100%'
                sx={{ minHeight: 240, borderRadius: 1 }}
            />
          </div>
      );
    }

    if (isPdf) {
      return (
          <object
              className='pdf'
              width='100%'
              height='100%'
              type='application/pdf'
              data={sourceDocument.url}
              aria-label={translate(
                  data,
                  'cc_statements.statement_import_modal.document',
                  'Statement document'
              )}
          />
      );
    }

    if (isImage) {
      return (
          <img
              src={sourceDocument.url}
              alt={sourceDocument.filename || ''}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
          />
      );
    }

    return (
        <div className='d-flex flex-column align-items-start gap-3 p-4'>
          <p className='mb-0 semi-faded'>
            {translate(
                data,
                'cc_statements.statement_import_modal.no_preview',
                'This statement format cannot be previewed.'
            )}
          </p>

          <a
              className='btn btn-secondary'
              href={sourceDocument.url}
              target='_blank'
              rel='noopener noreferrer'
          >
            {translate(
                data,
                'cc_statements.statement_import_modal.open_document',
                'Open document'
            )}
          </a>
        </div>
    );
  };

  const statementHeader = () => {
    if (fields.length === 0) {
      return (
          <p className='semi-faded mb-0'>
            {translate(
                data,
                'cc_statements.statement_import_modal.no_statement_header',
                'No statement header was extracted from this document.'
            )}
          </p>
      );
    }

    return (
        <div className='row g-3'>
          {fields.map((field) => (
              <div
                  key={field.key}
                  className={data.isMobile ? 'col-12' : 'col-6'}
              >
                <InvoiceFormItem
                    label={field.label}
                    alwaysUseMobileStyling
                    labelClassName='text-muted align-self-start'
                    data={data}
                    show
                >
                  <div className='fw-semibold'>
                    {field.value}
                  </div>
                </InvoiceFormItem>
              </div>
          ))}
        </div>
    );
  };

  return (
      <>
        <Modal
            title={
                reconciliation.filename ||
                translate(
                    data,
                    'cc_statements.statement_import_modal.title',
                    'Statement import'
                )
            }
            width='92vw'
            height='85vh'
            overflow='hidden'
            cancelAction={onClose}
            cancelText={translate(data, 'close', 'Close')}
            noProcessingLoading
        >
          <div
              className='invoice statement-import-modal bg-white d-flex'
              style={{
                height: '100%',
                minHeight: 0,
              }}
          >
            {!data.isMobile && (
                <CustomResizable
                    type='Invoice'
                    // A statement preview is not an invoice preview; sharing the
                    // default key let a width set on an invoice resize this pane.
                    storageKey='StatementImportPreview'
                    maxWidth='45vw'
                    minWidth='25vw'
                    // The pane fills the dialog. The 90vh default is sized for a
                    // full page and overflows an 85vh dialog.
                    height='100%'
                    // One divider between the panes, in the colour the rest of the
                    // modal uses - the default black border drew a second one.
                    border={{
                      borderTop: 'none',
                      borderRight: '1px solid #dee2e6',
                      borderBottom: 'none',
                      borderLeft: 'none',
                    }}
                >
                  <div
                      className='image-section position-relative'
                      style={{ height: '100%' }}
                  >
                    {documentPreview()}
                  </div>
                </CustomResizable>
            )}

            <div
                className='content-section d-flex flex-column flex-grow-1'
                style={{
                  minWidth: 0,
                  minHeight: 0,
                  height: '100%',
                }}
            >
              {data.isMobile && (
                  <div
                      className='statement-import-modal__mobile-document'
                      style={{
                        minHeight: 300,
                        borderBottom: '1px solid #dee2e6',
                      }}
                  >
                    {documentPreview()}
                  </div>
              )}

              <div
                  className='flex-grow-1'
                  style={{
                    minHeight: 0,
                    overflowY: 'auto',
                  }}
              >
                <div className='p-4'>
                  <div
                      className='d-flex flex-wrap align-items-center justify-content-between gap-3 pb-3 mb-4'
                      style={{
                        borderBottom: '1px solid #dee2e6',
                      }}
                  >
                    <div>
                      <div className='text-muted small mb-1'>
                        {translate(
                            data,
                            'cc_statements.statement_import_modal.card',
                            'Card'
                        )}
                      </div>

                      <div className='fw-semibold'>
                        {formatCard(reconciliation.card_account)}
                      </div>
                    </div>

                    <span
                        className={
                            `statement-imports__status ` +
                            `statement-imports__status--${reconciliation.status}`
                        }
                    >
                    {translate(
                        data,
                        `cc_statements.statement_imports.status.${reconciliation.status}`,
                        reconciliation.status
                    )}
                  </span>
                  </div>

                  {failed && (
                      <div className='alert alert-danger' role='alert'>
                        <div className='font-weight-bold'>
                          {translate(
                              data,
                              'cc_statements.statement_import_modal.failure_title',
                              'This statement could not be processed'
                          )}
                        </div>
                        <div>{failureReason}</div>
                      </div>
                  )}

                  <section>
                    <h4 className='mb-3'>
                      {translate(
                          data,
                          'cc_statements.statement_import_modal.statement_header',
                          'Statement header'
                      )}
                    </h4>

                    {statementHeader()}
                  </section>

                  {matchError && !linkingItem && !creatingItem && (
                      <div
                          className='alert alert-danger mt-4 mb-0'
                          role='alert'
                      >
                        {matchError}
                      </div>
                  )}

                  <StatementLinesTable
                      data={data}
                      items={detail?.items || []}
                      loading={!detail && !detailFailed}
                      loadFailed={detailFailed}
                      busyItemId={busyItemId}
                      onLink={(item) => {
                        setMatchError(null);
                        setLinkingItem(item);
                      }}
                      onUnlink={unlinkTransaction}
                  />
                </div>
              </div>
            </div>
          </div>
        </Modal>

        {linkingItem && (
            <LinkTransactionModal
                data={data}
                reconciliationId={reconciliationId}
                item={linkingItem}
                saving={busyItemId === linkingItem.id}
                error={matchError}
                onConfirm={linkTransaction}
                onCreate={() => {
                  // Hand the same line straight to the create form.
                  setCreatingItem(linkingItem);
                  setLinkingItem(null);
                  setMatchError(null);
                }}
                onClose={() => {
                  setLinkingItem(null);
                  setMatchError(null);
                }}
            />
        )}

        {creatingItem && (
            <CreateCardLineItemModal
                data={data}
                item={creatingItem}
                saving={busyItemId === creatingItem.id}
                error={matchError}
                onConfirm={createLineItem}
                onClose={() => {
                  setCreatingItem(null);
                  setMatchError(null);
                }}
            />
        )}
      </>
  );
};

export default inject('data')(observer(StatementImportModal));